import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';

import { ICartEntry, IServerOrder } from '@rbi-ctg/menu';
import { ActionLink } from 'components/action-button';
import CartItem from 'components/cart-item';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import useMediaQuery from 'hooks/use-media-query';
import { TimsRewardsPoints } from 'pages/account/tims-rewards/shared-onboarding-modal-components/tims-rewards-points';
import { useCustomizableContent } from 'state/customizable-content';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useOffersContext } from 'state/offers';
import { UIPattern } from 'state/offers/types';
import { OrderStatus, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { CartEntryType } from 'utils/cart';
import { mapPaymentMethodToLoyaltyPaymentMethods } from 'utils/payment';
import { relativeRoutes, routes } from 'utils/routing';

import { CartPromoCodes } from '../cart-promo-codes';
import CartSectionHeading from '../section-heading/cart-section-heading';

import CartContainer from './cart-container';
import CartDescription from './cart-description';
import { CartOffer, LoyaltyDiscountCartOffer } from './cart-offer';
import DonationContainer from './donation-container';
import { RewardDropdown } from './reward-dropdown';
import { CartItems } from './styled';
import { Cart, RewardsPointsContainer } from './styled.shared';
import theme from './theme';
import TotalsContainer from './totals-container';

interface IYourCartProps {
  //temp prop to be removed in next PR
  serverOrderCartEntries?: any;
  className?: string;
  checkoutPaymentMethod?: IPaymentMethod | null;
  orderStatus: OrderStatus | null;
  serverOrder: IServerOrder | null;
  readOnly?: boolean;
  redeemableItemsSet?: Set<string | unknown>;
  showAddItemsBtn?: boolean;
  showAddExtrasBtn?: boolean;
  showCartPromoCodes?: boolean;
  pricedAndAvailable?: boolean;
}

const YourCart: React.FC<IYourCartProps> = ({
  serverOrderCartEntries,
  className = '',
  checkoutPaymentMethod,
  orderStatus,
  readOnly = false,
  redeemableItemsSet = new Set(),
  serverOrder,
  showAddItemsBtn = true,
  showAddExtrasBtn = true,
  showCartPromoCodes = true,
  pricedAndAvailable = true,
}) => {
  const { cartEntries: localCartEntries, calculateCartTotalWithDiscount } = useOrderContext();

  //temp const to be removed in next pr with long term solution
  const cartEntries = serverOrderCartEntries ?? localCartEntries;

  const { serviceMode, isCatering } = useServiceModeContext();

  const offerCtx = useOffersContext();
  const {
    clearSelectedOffer,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
    offerValidationErrors = [],
    validateOfferRedeemable,
  } = offerCtx;
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyUser();
  const {
    queryLoyaltyUserSurpriseOffers,
    loyaltySurpriseOffersEnabled,
    evaluateLoyaltyUserIncentives,
  } = useLoyaltyContext();
  const { store } = useStoreContext();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const { requestLoyaltyCustomizableContent } = useCustomizableContent();
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const appliedLoyaltyRewardWithDiscountOffer = useAppSelector(
    selectors.loyalty.selectAppliedDiscountRewards
  );

  const loyaltyRewardsMap = useAppSelector(selectors.loyalty.selectAvailableLoyaltyRewardsMap);

  const dispatch = useAppDispatch();

  const onRemoveOffer = useCallback(() => {
    return clearSelectedOffer({ doLog: true, popToast: true });
  }, [clearSelectedOffer]);

  const cartOfferItem = useMemo(() => {
    if (selectedOffer && selectedOfferCartEntry) {
      return {
        ...selectedOfferCartEntry,
        price: selectedOfferPrice || 0,
      };
    }

    return null;
  }, [selectedOfferCartEntry, selectedOffer, selectedOfferPrice]);

  const appliedRewardsCount = useRef(0);
  const cartEntriesCount = useRef(0);

  const offerIsValid = offerValidationErrors.length === 0;
  const offerWasDeleted =
    !offerIsValid &&
    offerValidationErrors.some(({ code, condition }) => {
      return code === 'COUPON_EXISTS' && condition === false;
    });
  const textOfferRequirementsNotMet = formatMessage({
    id: offerWasDeleted ? 'incentiveFeedbackOfferNotAvailable' : 'cartOfferRequirementsNotMetError',
  });

  const showSelectedOffer =
    selectedOffer && selectedOfferCartEntry && selectedOffer?.uiPattern !== UIPattern.CART_PROMO;

  const { isFreeOrder } = usePaymentContext();

  useEffect(() => {
    if (selectedOffer && validateOfferRedeemable) {
      // serviceMode should always be selected at this point as is selected with store however as it is
      // nullable a check is needed
      if (serviceMode) {
        // validateOfferRedeemable will update the offer.offerValidationErrors
        validateOfferRedeemable({
          cart: {
            cartEntries,
            serviceMode,
            store,
          },
          selectedOffer,
          rbiOrderId: serverOrder?.rbiOrderId,
        });
      } else {
        // validateOfferRedeemable will update the offer.offerValidationErrors
        validateOfferRedeemable({
          selectedOffer,
          rbiOrderId: serverOrder?.rbiOrderId,
        });
      }
    }
  }, [cartEntries, serviceMode, selectedOffer, serverOrder, store, validateOfferRedeemable]);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  useEffect(requestLoyaltyCustomizableContent, [requestLoyaltyCustomizableContent]);

  useEffect(() => {
    // Resetting offer feedback on mount to avoid carrying over feedback from offer list
    dispatch(actions.loyalty.resetOfferFeedbackMap());
  }, [dispatch]);

  const runIncentivesEvaluation = useCallback(() => {
    if (loyaltyUser?.id && appliedLoyaltyOffers.length) {
      const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

      evaluateLoyaltyUserIncentives(
        loyaltyUser?.id,
        appliedLoyaltyOffers,
        cartEntries,
        appliedLoyaltyRewards,
        positiveCartTotal,
        paymentMethod
      );
    }
  }, [
    loyaltyUser,
    appliedLoyaltyOffers,
    checkoutPaymentMethod,
    evaluateLoyaltyUserIncentives,
    cartEntries,
    appliedLoyaltyRewards,
    positiveCartTotal,
  ]);

  useEffect(() => {
    if (pricedAndAvailable) {
      const validCartEntriesCount =
        // Donation entries should not be taken into account for querying offers
        cartEntries?.filter(({ isDonation }: ICartEntry) => !isDonation).length ?? 0;
      const appliedRewardsLength = Object.keys(appliedLoyaltyRewards).length;
      const entryAdded = cartEntriesCount.current < validCartEntriesCount;
      const rewardUnapplied = appliedRewardsCount.current > appliedRewardsLength;
      const shouldFetchOffers = entryAdded || rewardUnapplied;
      const cartEntriesWithoutOffers = cartEntries?.filter(
        ({ type }: ICartEntry) =>
          ![
            CartEntryType.offerCombo,
            CartEntryType.offerDiscount,
            CartEntryType.offerItem,
          ].includes(type)
      );

      runIncentivesEvaluation();

      if (loyaltySurpriseOffersEnabled && shouldFetchOffers && loyaltyUser?.id) {
        queryLoyaltyUserSurpriseOffers({
          loyaltyId: loyaltyUser?.id,
          cartEntries: cartEntriesWithoutOffers, // passing cart entries without offers to exclude upsize swap suggestions for offer benefits
          appliedRewards: appliedLoyaltyRewards,
          appliedLoyaltyOffers,
        });
      }

      appliedRewardsCount.current = appliedRewardsLength;
      cartEntriesCount.current = validCartEntriesCount;
    }
  }, [
    queryLoyaltyUserSurpriseOffers,
    cartEntries,
    appliedLoyaltyRewards,
    loyaltySurpriseOffersEnabled,
    loyaltyUser,
    runIncentivesEvaluation,
    appliedLoyaltyOffers,
    pricedAndAvailable,
  ]);

  const isMobile = useMediaQuery('mobile');

  useEffect(() => {
    if (serverOrder?.cart?.totalCents !== undefined) {
      isFreeOrder(serverOrder?.cart?.totalCents as number);
    }
  }, [serverOrder?.cart.totalCents]);

  return (
    <CartContainer className={className} isCatering={isCatering}>
      <Cart>
        <CartSectionHeading isCatering={isCatering} />

        {!isMobile && enableTHRewardsTierToggleUI && (
          <RewardsPointsContainer>
            <TimsRewardsPoints points={loyaltyDetails?.points ?? 0} />
          </RewardsPointsContainer>
        )}
        <RewardDropdown />
        <CartDescription aria-labelledby="cart-section-heading-label">
          <CartItems data-testid="cart-items">
            {showSelectedOffer && selectedOffer && (
              <CartOffer
                onRemove={onRemoveOffer}
                textOfferRequirementsNotMet={textOfferRequirementsNotMet}
                selectedOffer={selectedOffer}
                isValid={offerIsValid}
              />
            )}
            {appliedLoyaltyDiscountOffer && (
              <LoyaltyDiscountCartOffer selectedLoyaltyOffer={appliedLoyaltyDiscountOffer} />
            )}
            {appliedLoyaltyRewardWithDiscountOffer && Object.keys(loyaltyRewardsMap).length > 0 && (
              <CartItem
                item={
                  loyaltyRewardsMap[appliedLoyaltyRewardWithDiscountOffer.rewardBenefitId] as any
                }
                rewardApplied={true}
              />
            )}
            {cartOfferItem && (
              <CartItem
                item={cartOfferItem}
                key={
                  (selectedOfferCartEntry && selectedOfferCartEntry.cartId) ||
                  'this-key-is-not-necessary'
                }
                selectedOfferId={offerCtx.selectedOffer?._id}
                rewardApplied={offerIsValid}
              />
            )}
            {cartEntries.map((item: ICartEntry) => {
              const isRedeemable = redeemableItemsSet.has(item.cartId);
              const isApplied = appliedLoyaltyRewards[item.cartId]?.timesApplied > 0;
              const showQuantity = serverOrder !== null && serverOrder?.catering !== undefined;

              return (
                <CartItem
                  item={item}
                  key={item.cartId || item._id}
                  readOnly={readOnly}
                  rewardApplied={isApplied || isRedeemable}
                  showReadOnlyQuantity={showQuantity}
                />
              );
            })}
          </CartItems>
          {(showAddItemsBtn || showAddExtrasBtn) && (
            <Box margin="1rem auto 0" justify="space-evenly">
              {showAddExtrasBtn && enableAddExtras && (
                <ActionLink
                  to={relativeRoutes.addExtra}
                  startIcon={<Icon icon="add" color="white" aria-hidden />}
                  variant={theme.addItemsVariant}
                  transparent={theme.addItemsTransparentBackground}
                  size={
                    showAddItemsBtn && showAddExtrasBtn ? theme.doubleBtnSize : theme.singleBtnSize
                  }
                >
                  {formatMessage({ id: 'addExtras' })}
                </ActionLink>
              )}

              {showAddItemsBtn && (
                <ActionLink
                  to={routes.menu}
                  startIcon={<Icon icon="add" color="white" aria-hidden />}
                  variant={theme.addItemsVariant}
                  transparent={theme.addItemsTransparentBackground}
                  size={
                    showAddItemsBtn && showAddExtrasBtn ? theme.doubleBtnSize : theme.singleBtnSize
                  }
                  eventAttributes={{
                    Name: formatMessage({ id: 'addItemsLowerCase' }),
                  }}
                >
                  {formatMessage({ id: 'addItems' })}
                </ActionLink>
              )}
            </Box>
          )}

          <DonationContainer />

          {/* promo code checkout content flow */}
          {showCartPromoCodes && !isSignUpAfterCart && <CartPromoCodes />}
          <TotalsContainer
            orderStatus={orderStatus}
            serverOrder={serverOrder}
            serviceMode={serviceMode}
            rewardApplied={cartEntries?.some((item: ICartEntry) =>
              redeemableItemsSet.has(item.cartId)
            )}
          />
        </CartDescription>
      </Cart>
    </CartContainer>
  );
};

export default React.memo(YourCart);
